import { auth, uid } from '@/firebase'

import {
  CalendarService,
  FSTeam,
  NitteCalendarEvent,
  NitteServiceType,
  formatFullDate
} from '@WombatTechnology/nitte-time-lib'
import { isNull } from 'es-toolkit/compat'
import { handleApiError } from './apiError'
import { EventResponse, parseEventResponse } from './apiResponseParser'
import { openExternalLink } from './openExternalLink'

export const getIdToken = async (): Promise<string> => {
  const user = auth.currentUser
  if (isNull(user)) {
    return ''
  }
  // WARNING:
  // このflagをtrueにしているとメソッドをコールした瞬間に
  // エラーなしでDOMが全て消失してしまうバグがある。
  return user.getIdToken(false)
}

// ⚪︎
export const deleteUser = async (message: string): Promise<void> => {
  const idToken = await getIdToken()
  const url = `${import.meta.env.VITE_APP_CLOUD_FUNCTION_URL}/internal_api/users/me`
  const requestBody = { message }
  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`
    },
    body: JSON.stringify(requestBody)
  })

  if (!response.ok) {
    await handleApiError(response, { url, method: 'DELETE', requestBody })
  }
}

// ⚪︎
export const createTeam = async ({
  name,
  invitedMemberEmails,
  service,
  usagePurpose,
  usagePurposeDetail,
  referralSource,
  referralSourceDetail
}: {
  name: string
  invitedMemberEmails: string[]
  service: NitteServiceType
  usagePurpose: FSTeam['usagePurpose']
  // 'other' のときの自由入力テキスト。それ以外は null
  usagePurposeDetail: FSTeam['usagePurposeDetail']
  // Nitteを知ったきっかけ。人材紹介のチーム作成では null
  referralSource: FSTeam['referralSource']
  // 'other' のときの自由入力テキスト。それ以外は null
  referralSourceDetail: FSTeam['referralSourceDetail']
}): Promise<string> => {
  const idToken = await getIdToken()
  const url = `${import.meta.env.VITE_APP_CLOUD_FUNCTION_URL}/internal_api/teams`
  const requestBody = {
    name,
    invitedMemberEmails,
    service,
    usagePurpose,
    usagePurposeDetail,
    referralSource,
    referralSourceDetail
  }
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`
    },
    body: JSON.stringify(requestBody)
  })

  if (!response.ok) {
    await handleApiError(response, { url, method: 'POST', requestBody })
  }

  const data = await response.json()
  return data.docId
}

// ⚪︎
export const deleteTeam = async (teamId: string) => {
  const idToken = await getIdToken()
  const url = `${import.meta.env.VITE_APP_CLOUD_FUNCTION_URL}/internal_api/teams/${teamId}`
  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${idToken}`
    }
  })

  if (!response.ok) {
    await handleApiError(response, { url, method: 'DELETE' })
  }
}

// ⚪︎
export const disconnectCalendarAccount = async (
  accountId: string,
  service: CalendarService
) => {
  const idToken = await getIdToken()
  const url = `${import.meta.env.VITE_APP_CLOUD_FUNCTION_URL}/internal_api/calendar_accounts`
  const requestBody = { accountId, service }
  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`
    },
    body: JSON.stringify(requestBody)
  })

  if (!response.ok) {
    await handleApiError(response, { url, method: 'DELETE', requestBody })
  }

  const data = await response.json()
  return {
    success: data.success,
    message: data.message,
    error_message: data.error_message
  }
}

// ⚪︎
export const renewUserNotificationChannel = async (
  uid: string
): Promise<any> => {
  const url = `${import.meta.env.VITE_APP_CLOUD_FUNCTION_URL}/internal_api/users/${uid}/notification_channels/renew`
  const response = await fetch(url)

  if (!response.ok) {
    await handleApiError(response, { url, method: 'GET' })
  }

  return response.json()
}

// ⚪︎
export const renewCalendarNotificationChannel = async (
  calendarId: string
): Promise<any> => {
  const url = `${import.meta.env.VITE_APP_CLOUD_FUNCTION_URL}/internal_api/calendars/${calendarId}/notification_channels/renew`
  const response = await fetch(url)

  // 404 は「その room の通知チャンネルがまだ未登録 (または切断済み room の参照残り)」という
  // 想定内の状態。これは MS 会議室が subscribe 非対応なためページアクセス時に renew する
  // best-effort な最適化であり、404 でも room の空き時間は onSnapshot 経路で表示され
  // ゲストの予約体験は壊れない。Sentry ノイズになるので静かに無視する (throw も送信もしない)。
  if (response.status === 404) {
    return
  }

  if (!response.ok) {
    await handleApiError(response, { url, method: 'GET' })
  }

  return response.json()
}

// ⚪︎
export const createCalendarNotificationChannel = async (
  accountId: string,
  calendarId: string
): Promise<any> => {
  const idToken = await getIdToken()
  const url = `${import.meta.env.VITE_APP_CLOUD_FUNCTION_URL}/internal_api/calendar_accounts/${accountId}/calendars/${calendarId}/notification_channels`
  const requestBody = {}
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`
    },
    body: JSON.stringify(requestBody)
  })

  if (!response.ok) {
    await handleApiError(response, { url, method: 'POST', requestBody })
  }

  return response.json()
}

// ⚪︎
export const createKeywordPeriods = async (
  uid: string,
  keyword: string
): Promise<any> => {
  const url = `${import.meta.env.VITE_APP_CLOUD_FUNCTION_URL}/internal_api/users/${uid}/keyword_periods`
  const requestBody = { keyword }
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(requestBody)
  })

  if (!response.ok) {
    await handleApiError(response, { url, method: 'POST', requestBody })
  }

  return response.json()
}

// ⚪︎
export const syncCalendarAvailablePeriods = async (calendarId: string) => {
  const url = `${import.meta.env.VITE_APP_CLOUD_FUNCTION_URL}/internal_api/calendars/${calendarId}/available_periods/sync`
  const response = await fetch(url)

  // 404 は「その room がまだ同期されていない」という想定内の状態。これは MS 会議室が
  // subscribe 非対応なためページアクセス時に同期をかける best-effort な最適化であり、
  // 404 でも room の空き時間は onSnapshot 経路で表示されゲストの予約体験は壊れない。
  // Sentry ノイズになるので静かに無視する (throw も送信もしない)。
  if (response.status === 404) {
    return
  }

  if (!response.ok) {
    await handleApiError(response, { url, method: 'GET' })
  }
}

// ⚪︎
export const fetchUserEvents = async (
  start: Date,
  end: Date
): Promise<NitteCalendarEvent[]> => {
  const idToken = await getIdToken()
  const url = `${import.meta.env.VITE_APP_CLOUD_FUNCTION_URL}/internal_api/users/me/events?start=${formatFullDate(start)}&end=${formatFullDate(end)}`
  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${idToken}`
    }
  })

  if (!response.ok) {
    await handleApiError(response, { url, method: 'GET' })
  }

  const data = await response.json()
  const events = data.events as EventResponse[]
  return events.map((ev) => parseEventResponse(ev))
}

// ⚪︎
export const fetchCalendarTimeZone = async (
  uid: string
): Promise<{ timeZone: string }> => {
  const url = `${import.meta.env.VITE_APP_CLOUD_FUNCTION_URL}/internal_api/users/${uid}/calendar_time_zone`
  const response = await fetch(url)

  if (!response.ok) {
    await handleApiError(response, { url, method: 'GET' })
  }

  return response.json()
}

// ⚪︎
export const fetchTeamId = async (
  uid: string
): Promise<{ teamId: string | null }> => {
  const url = `${import.meta.env.VITE_APP_CLOUD_FUNCTION_URL}/internal_api/users/${uid}/team_id`
  const response = await fetch(url)

  if (!response.ok) {
    await handleApiError(response, { url, method: 'GET' })
  }

  return response.json()
}

// ⚪︎
export const fetchJapaneseHolidays = async (
  start: Date,
  end: Date
): Promise<NitteCalendarEvent[]> => {
  const url = `${import.meta.env.VITE_APP_CLOUD_FUNCTION_URL}/internal_api/national_holidays?start=${formatFullDate(start)}&end=${formatFullDate(end)}`
  const response = await fetch(url)

  if (!response.ok) {
    await handleApiError(response, { url, method: 'GET' })
  }

  const data = await response.json()
  const events = data.holidays as EventResponse[]
  return events.map((ev) => parseEventResponse(ev))
}

// ⚪︎
export const updateChatworkRooms = async (): Promise<void> => {
  const idToken = await getIdToken()
  const url = `${import.meta.env.VITE_APP_CLOUD_FUNCTION_URL}/internal_api/chatwork_rooms`
  const requestBody = {}
  const response = await fetch(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`
    },
    body: JSON.stringify(requestBody)
  })

  if (!response.ok) {
    await handleApiError(response, { url, method: 'PATCH', requestBody })
  }
}

// カレンダー権限が不足しているユーザー本人 (uid) に権限付与を促すメールを送る。
// 連携解除 (invalid_grant) 等で scope が空になったユーザーを含むページを
// 第三者が開いたときに PeriodLoadErrorBox から呼ばれる。
export const sendInsufficientPermissionMail = async (uid: string) => {
  const url = `${import.meta.env.VITE_APP_CLOUD_FUNCTION_URL}/internal_api/users/${uid}/send_insufficient_permission_mail`
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({})
  })

  if (!response.ok) {
    await handleApiError(response, { url, method: 'POST' })
  }
}

// ⚪︎
export const notifyPageUnavailable = async (uid: string, pageId: string) => {
  const url = `${import.meta.env.VITE_APP_CLOUD_FUNCTION_URL}/internal_api/users/${uid}/pages/${pageId}/notify_unavailable`
  const requestBody = {}
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(requestBody)
  })

  // プレビュー (pageId === 'preview') は固定センチネル値で BookingPageMetaData が存在せず、
  // 旧 backend では必ず 404 を返していた。preview はオーナー本人の下書き表示なので、
  // 404 でも通知不要な想定内の状態として静かに無視する (Sentry ノイズ NITTE-34 対策)。
  // preview 以外の実ページで本当に通知すべき 404 は握りつぶさない。
  if (pageId === 'preview' && response.status === 404) {
    return
  }

  if (!response.ok) {
    await handleApiError(response, { url, method: 'POST', requestBody })
  }

  return response.json()
}

// ⚪︎
export const syncCalendars = async (uid: string) => {
  const url = `${import.meta.env.VITE_APP_CLOUD_FUNCTION_URL}/internal_api/users/${uid}/calendars/sync`
  const response = await fetch(url)

  if (!response.ok) {
    await handleApiError(response, { url, method: 'GET' })
  }

  return response.json()
}

export const deauthService = (
  service: 'slack' | 'slack_bot' | 'teams' | 'teams_chat',
  nitteServiceType: 'nitte' | 'nitte-mediatory' = 'nitte'
) => {
  const baseUrl = import.meta.env.VITE_APP_CLOUD_FUNCTION_URL
  const queryString = `uid=${uid()!}&service=${nitteServiceType}`
  // NOTE: teams_chat はバックエンドに対応する deauth endpoint が存在しないため旧 URL を保持
  const pathByService: Record<typeof service, string> = {
    slack_bot: `/internal_api/auth/slack_bot/deauth`,
    slack: `/internal_api/auth/slack_legacy/deauth`,
    teams: `/internal_api/auth/teams/deauth`,
    teams_chat: `/deauth_teams_chat`
  }
  const deauthUrl = `${baseUrl}${pathByService[service]}?${queryString}`
  openExternalLink(deauthUrl)
}

// ⚪︎
export const testWebhook = async (webhook: {
  url: string
  securityToken: string
}) => {
  const url = `${import.meta.env.VITE_APP_CLOUD_FUNCTION_URL}/internal_api/webhooks/test`
  const requestBody = { webhook }
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(requestBody)
  })

  if (!response.ok) {
    await handleApiError(response, { url, method: 'POST', requestBody })
  }

  return response.json()
}

// ⚪︎
export const switchTeam = async (teamId: string) => {
  const idToken = await getIdToken()
  const url = `${import.meta.env.VITE_APP_CLOUD_FUNCTION_URL}/internal_api/teams/${teamId}/switch`
  const requestBody = {}
  const response = await fetch(url, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`
    },
    // NOTE:
    // dataを含めないと、ジェイックのセキュリティツールが411(Length Required)を返して、エラーになったので空データを含めておく。
    // @see https://claude.ai/chat/39243872-c5ba-4790-8ccb-93f96bf21ccc
    body: JSON.stringify(requestBody)
  })

  if (!response.ok) {
    await handleApiError(response, { url, method: 'PATCH', requestBody })
  }
}

// ⚪︎
export const testSendBookingMailTemplate = async (
  teamId: string,
  titleTemplate: string,
  bodyTemplate: string,
  mailTo: string
) => {
  const idToken = await getIdToken()
  const url = `${import.meta.env.VITE_APP_CLOUD_FUNCTION_URL}/internal_api/teams/${teamId}/booking_mail_templates/test_send`
  const requestBody = { titleTemplate, bodyTemplate, mailTo }
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`
    },
    body: JSON.stringify(requestBody)
  })

  if (!response.ok) {
    await handleApiError(response, { url, method: 'POST', requestBody })
  }
}

type ParseDateTimeResult = {
  original: string
  success: boolean
  start: string | null
  end: string | null
  label: string | null
  error: string | null
}

export const parseDateTimePeriods = async (
  text: string,
  divide?: { minutes: number; step?: number }
): Promise<{ candidates: ParseDateTimeResult[] }> => {
  const url = `${import.meta.env.VITE_APP_CLOUD_FUNCTION_URL}/internal_api/parse`
  const requestBody = { text, ...(divide ? { divide } : {}) }
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(requestBody)
  })

  if (!response.ok) {
    await handleApiError(response, { url, method: 'POST', requestBody })
  }

  return response.json()
}
